import { compileShader, linkProgram } from './gl-program.helpers';
import type { ExtrusionGlResources } from './types';

/**
 * Initialize WebGL program + buffer for the extrusion overlay.
 * @param gl WebGL context provided by Mapbox custom layer
 */
export function initExtrusionGl(
  gl: WebGLRenderingContext,
): ExtrusionGlResources {
  const vsSource = `
    precision highp float;
    attribute vec3 a_pos;
    attribute vec3 a_normal;
    attribute vec4 a_color;
    uniform mat4 u_matrix;
    uniform vec3 u_light_dir;
    uniform float u_ambient;
    uniform float u_strength;
    varying vec4 v_color;
    void main() {
      gl_Position = u_matrix * vec4(a_pos, 1.0);
      vec3 n = normalize(a_normal);
      vec3 l = normalize(u_light_dir);
      // Henrik: abs() makes shading stable even if polygon winding flips.
      float diffuse = pow(abs(dot(n, l)), 0.85);
      // Keep even "sunny" faces from drifting grey; wrap a bit toward 1.0.
      diffuse = 0.35 + 0.65 * diffuse;
      float shaded = mix(1.0, diffuse, u_strength);
      float light = max(shaded, u_ambient);
      // Premultiply alpha to match Mapbox's premultiplied canvas blending.
      v_color = vec4(a_color.rgb * light * a_color.a, a_color.a);
    }
  `;

  const fsSource = `
    precision mediump float;
    varying vec4 v_color;
    void main() {
      gl_FragColor = v_color;
    }
  `;

  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = linkProgram(gl, vs, fs);

  const buffer = gl.createBuffer();
  const wallBuffer = gl.createBuffer();
  const roofBuffer = gl.createBuffer();
  if (!buffer || !wallBuffer || !roofBuffer) {
    gl.deleteProgram(program);
    if (buffer) gl.deleteBuffer(buffer);
    if (wallBuffer) gl.deleteBuffer(wallBuffer);
    if (roofBuffer) gl.deleteBuffer(roofBuffer);
    throw new Error('Failed to create WebGL buffer for extrusion');
  }

  const aPosition = gl.getAttribLocation(program, 'a_pos');
  const aNormal = gl.getAttribLocation(program, 'a_normal');
  const aColor = gl.getAttribLocation(program, 'a_color');
  const uMatrix = gl.getUniformLocation(program, 'u_matrix');
  const uLightDir = gl.getUniformLocation(program, 'u_light_dir');
  const uAmbient = gl.getUniformLocation(program, 'u_ambient');
  const uStrength = gl.getUniformLocation(program, 'u_strength');

  if (
    aPosition < 0 ||
    aNormal < 0 ||
    aColor < 0 ||
    !uMatrix ||
    !uLightDir ||
    !uAmbient ||
    !uStrength
  ) {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    throw new Error('Failed to resolve extrusion shader locations');
  }

  return {
    program,
    buffer,
    wallBuffer,
    roofBuffer,
    aPosition,
    aNormal,
    aColor,
    uMatrix,
    uLightDir,
    uAmbient,
    uStrength,
  };
}
